import type {
  LayoutEdge,
  LayoutNode,
  LayoutResult,
  OccupiedRect,
  Point,
  PositionedNode,
} from './types'

const GAP = 40
const LEVEL_GAP = 64

const shapes = (
  nodes: PositionedNode[],
  edges: LayoutEdge[]
): OccupiedRect[] => [
  ...nodes.map((p) => ({
    left: p.x - 12,
    right: p.x + p.width + 12,
    top: p.y - 12,
    bottom: p.y + p.height + p.below + 12,
  })),
  ...edges.flatMap((e) =>
    e.points.slice(1).map((b, i) => {
      const a = e.points[i]
      return {
        left: Math.min(a[0], b[0]) - 6,
        right: Math.max(a[0], b[0]) + 6,
        top: Math.min(a[1], b[1]) - 6,
        bottom: Math.max(a[1], b[1]) + 6,
      }
    })
  ),
]
const moveEdges = (edges: LayoutEdge[], dx: number, dy: number): LayoutEdge[] =>
  edges.map((e) => ({
    ...e,
    points: e.points.map(([x, y]): Point => [x + dx, y + dy]),
  }))
const normalize = (
  nodes: PositionedNode[],
  edges: LayoutEdge[]
): LayoutResult => {
  const left = Math.min(...nodes.map((p) => p.x))
  return {
    nodes: nodes.map((p) => ({ ...p, x: p.x - left })),
    edges: moveEdges(edges, -left, 0),
    width: Math.max(...nodes.map((p) => p.x + p.width)) - left,
    height: Math.max(...nodes.map((p) => p.y + p.height + p.below)),
  }
}

// Include incoming stems in the contour: packing only cards permits crossed lines.
const pack = (node: LayoutNode, maxDrop: number, depth = 0): LayoutResult => {
  if (!node.children.length) {
    return {
      nodes: [{ ...node, x: 0, y: 0 }],
      edges: [],
      width: node.width,
      height: node.height + node.below,
    }
  }
  const placed: LayoutResult[] = []
  const occupied: OccupiedRect[] = []
  for (const child of node.children) {
    const subtree = pack(child, maxDrop, depth + 1)
    const last = placed.at(-1)?.nodes[0]
    const offsets =
      last && subtree.nodes.length > 1
        ? [
            ...new Set([
              0,
              ...Array.from(
                { length: Math.floor(maxDrop / 32) },
                (_, i) => (i + 1) * 32
              ),
              maxDrop,
            ]),
          ]
        : [0]
    // Siblings often query the same vertical bands. Separation depends only on
    // the furthest occupied right edge in that band, not on the candidate x.
    const rightByBand = new Map<string, number>()
    const rightInBand = (top: number, bottom: number) => {
      const key = `${top}:${bottom}`
      const cached = rightByBand.get(key)
      if (cached !== undefined) {
        return cached
      }
      const right = occupied.reduce(
        (value, rect) =>
          rect.top < bottom && top < rect.bottom
            ? Math.max(value, rect.right)
            : value,
        -Infinity
      )
      rightByBand.set(key, right)
      return right
    }
    const candidates = offsets.map((drop) => {
      const y = node.height + LEVEL_GAP + drop
      const root = subtree.nodes[0]
      const center = root.x + root.width / 2
      const nodes = subtree.nodes.map((p, i) => {
        const positionY =
          depth === 0 && i === 0 ? node.height + LEVEL_GAP : p.y + y
        return { ...p, y: positionY }
      })
      const edges = moveEdges(subtree.edges, 0, y).map((e) =>
        depth === 0 && e.from === root.id
          ? {
              ...e,
              points: e.points.map(
                ([x, py], i): Point => [
                  x,
                  i === 0 ? node.height + LEVEL_GAP + root.height : py,
                ]
              ),
            }
          : e
      )
      const incoming: LayoutEdge = {
        from: node.id,
        to: root.id,
        points: [
          [center, node.height + LEVEL_GAP / 2],
          [center, y],
        ],
      }
      const contour = shapes(nodes, [...edges, incoming])
      const separation = last ? last.x + last.width + GAP - root.x : 0
      const dx = contour.reduce(
        (offset, rect) =>
          Math.max(offset, rightInBand(rect.top, rect.bottom) + 16 - rect.left),
        separation
      )
      const movedNodes = nodes.map((p) => ({ ...p, x: p.x + dx }))
      const all = [...placed.flatMap((p) => p.nodes), ...movedNodes]
      const width =
        Math.max(...all.map((p) => p.x + p.width)) -
        Math.min(...all.map((p) => p.x))
      const height = Math.max(...all.map((p) => p.y + p.height + p.below))
      return {
        nodes: movedNodes,
        edges: moveEdges(edges, dx, 0),
        width,
        height,
        contour: contour.map((p) => ({
          ...p,
          left: p.left + dx,
          right: p.right + dx,
        })),
        score: width + 0.65 * (height - node.height) + 0.025 * drop,
      }
    })
    const best = candidates.reduce((a, b) => (b.score < a.score - 0.01 ? b : a))
    placed.push(best)
    occupied.push(...best.contour)
  }
  const first = placed[0].nodes[0]
  const last = placed[placed.length - 1].nodes[0]
  const center = (first.x + first.width / 2 + last.x + last.width / 2) / 2
  const nodes = [
    { ...node, x: center - node.width / 2, y: 0 },
    ...placed.flatMap((p) => p.nodes),
  ]
  const edges: LayoutEdge[] = [
    ...placed.flatMap((p) => p.edges),
    ...placed.map((p) => {
      const child = p.nodes[0]
      const x = child.x + child.width / 2
      return {
        from: node.id,
        to: child.id,
        points: [
          [center, node.height],
          [center, node.height + LEVEL_GAP / 2],
          [x, node.height + LEVEL_GAP / 2],
          [x, child.y],
        ] as Point[],
      }
    }),
  ]
  return normalize(nodes, edges)
}

export const createCompactLayout = (
  root: LayoutNode,
  maxDrop = 384
): LayoutResult => {
  return pack(root, Math.max(0, Math.min(640, maxDrop)))
}
